import React, { useCallback, useMemo } from 'react';
import { isUndefined, set } from 'lodash/fp';
import classnames from 'classnames';

import styles from './SystemMaintenance.module.scss';
import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { Toggle } from '../../shared/toggle';
import { saveSystemMaintenanceStarted, getCurrentSystemMaintenance, fetchSystemMaintenanceStarted, getIsUpdatingSystemMaintenance, leftMenuTopLevel, SystemMaintenance as SystemMaintenanceType, rightMenuTopLevel, systemMaintenanceOptions, SystemMaintenanceUI } from './store';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { Spinner } from '../../shared/spinner/Spinner';

export const SystemMaintenance: React.FC = () => {
    useFetchStarted([fetchSystemMaintenanceStarted()]);
    const dispatch = useAppDispatch();

    const currentSystemMaintenance = useAppSelector(getCurrentSystemMaintenance);
    const isUpdatingSystemMaintenance = useAppSelector(getIsUpdatingSystemMaintenance);

    const onClick = useCallback((systemMaintenanceId: number, isDisabled: number) => dispatch(saveSystemMaintenanceStarted([systemMaintenanceId], isDisabled)), [dispatch]);

    const parentOptionChecked = useCallback((systemMaintenanceId: number) => currentSystemMaintenance.find(systemMaintenance => systemMaintenance.systemMaintenanceId === systemMaintenanceId)!.isDisabled === 1, [currentSystemMaintenance]);
    const childOptionChecked = useCallback((parentSystemMaintenanceId: number, systemMaintenanceId: number) => {
        const parentIsDisabled = currentSystemMaintenance.find(systemMaintenance => systemMaintenance.systemMaintenanceId === parentSystemMaintenanceId)!.isDisabled === 1;
        const currentChildIsDisabled = currentSystemMaintenance.find(systemMaintenance => systemMaintenance.systemMaintenanceId === systemMaintenanceId)!.isDisabled === 1;
        return parentIsDisabled || currentChildIsDisabled;
    }, [currentSystemMaintenance]);

    const getSideMenuOptions = useCallback((menuOptions: SystemMaintenanceType[]) => {
        const topLevelIds = currentSystemMaintenance.filter(({ systemMaintenanceId }) => menuOptions.includes(systemMaintenanceId));
        return topLevelIds.reduce((acc: SystemMaintenanceUI[], cur) => {
            const subSections = systemMaintenanceOptions[cur.systemMaintenanceId].subSections;
            if (!isUndefined(subSections)) {
                const currentSubSections = currentSystemMaintenance.filter(({ systemMaintenanceId }) => subSections.includes(systemMaintenanceId));
                const withSubSections: SystemMaintenanceUI = set('subSections', currentSubSections, cur);
                acc = [...acc, withSubSections];
            } else {
                acc = [...acc, cur];
            }
            return acc;
        }, []);
    }, [currentSystemMaintenance]);

    const leftMenuOptions = useMemo(() => getSideMenuOptions(leftMenuTopLevel), [getSideMenuOptions]);
    const rightMenuOptions = useMemo(() => getSideMenuOptions(rightMenuTopLevel), [getSideMenuOptions]);

    const internetGatewayOpenOptionChecked = useMemo(() => currentSystemMaintenance.find(({ systemMaintenanceId }) => systemMaintenanceId === SystemMaintenanceType.INTERNET_GATEWAY_OPEN)?.isDisabled === 1 || false, [currentSystemMaintenance]);
    const internetGatewayOpenOptionLabel = useMemo(() => currentSystemMaintenance.find(({ systemMaintenanceId }) => systemMaintenanceId === SystemMaintenanceType.INTERNET_GATEWAY_OPEN)?.label || '', [currentSystemMaintenance]);

    const sideMenu = useCallback((menuOptions: SystemMaintenanceUI[], label: string) => (
        <div className={styles.menuWrapper}>
            <div className={styles.sectionHeader}>{label}</div>
            <div className={styles.options}>
                {menuOptions.map(({ label, systemMaintenanceId, isDisabled, subSections }) => (
                    <div key={systemMaintenanceId} className={styles.option}>
                        <div className={styles.optionToggleAndLabel}>
                            <Toggle checked={parentOptionChecked(systemMaintenanceId)} onChange={() => onClick(systemMaintenanceId, isDisabled === 1 ? 0 : 1)} />
                            <div className={styles.optionLabel}>{label}</div>
                        </div>
                        {subSections && subSections.map(subSection => (
                            <div key={subSection.systemMaintenanceId} className={styles.subOption}>
                                <Toggle checked={childOptionChecked(systemMaintenanceId, subSection.systemMaintenanceId)} onChange={() => onClick(subSection.systemMaintenanceId, subSection.isDisabled === 1 ? 0 : 1)} disabled={parentOptionChecked(systemMaintenanceId)} />
                                <div className={classnames(styles.subOptionLabel, { [styles.subOptionDisabled]: parentOptionChecked(systemMaintenanceId) })}>{subSection.label}</div>
                            </div>
                        ))
                        }
                    </div>
                ))}
            </div>
        </div>
    ), [parentOptionChecked, childOptionChecked, onClick]);

    const body = useMemo(() => {
        if (isUpdatingSystemMaintenance) {
            return <Spinner />;
        }
        return (
            <div className={styles.body}>
                {sideMenu(leftMenuOptions, 'User Menu')}
                <div className={styles.internetGatewayWrapper}>
                    <Toggle checked={internetGatewayOpenOptionChecked} onChange={() => onClick(SystemMaintenanceType.INTERNET_GATEWAY_OPEN, internetGatewayOpenOptionChecked ? 0 : 1)} />
                    <div className={styles.optionLabel}>{internetGatewayOpenOptionLabel}</div>
                </div>
                {sideMenu(rightMenuOptions, 'Admin Menu')}
            </div>
        );
    }, [isUpdatingSystemMaintenance, sideMenu, leftMenuOptions, internetGatewayOpenOptionChecked, internetGatewayOpenOptionLabel, rightMenuOptions, onClick]);

    return (
        <div className={styles.systemMaintenanceWrapper}>
            <div className={styles.systemMaintenanceTitleContainer}>
                <div className={styles.systemMaintenanceTitle}>System Maintenance</div>
            </div>
            {body}
        </div>

    );
};
